import { inject, Injectable, ViewContainerRef } from '@angular/core';
import { ConfirmDialogComponent, HlmDialogOpener, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { ProjectsFacade } from '../domain/application/projects.facade';

const DEMO_NOTICE: ConfirmDialogContext = {
  title: 'Это демо-проект',
  message: 'Скачивание фотографий и макетов недоступно в демо-проекте',
  confirmLabel: 'Понятно',
  cancelLabel: null,
};

/** Single source for the demo-project download block: every download entry gates through `ensureNotDemo`,
 *  and the share-dialog watermark lock reads the same `isDemo` flag. */
@Injectable({ providedIn: 'root' })
export class DemoDownloadGuardService {
  private readonly facade = inject(ProjectsFacade);
  private readonly opener = inject(HlmDialogOpener);

  /** `true` when the download may proceed. On a demo project shows the «Это демо-проект» notice and returns `false`.
   *  A classified rare-conditional: on a normal project the check passes silently and no dialog exists. */
  async ensureNotDemo(vcr: ViewContainerRef | undefined, projectId: string | null | undefined): Promise<boolean> {
    const ref = await this.opener.openAfterCheck<ConfirmDialogContext>(ConfirmDialogComponent, vcr, async () =>
      (await this.isDemo(projectId)) ? DEMO_NOTICE : null,
    );
    return ref === undefined;
  }

  /** The notice alone — for flows that already know the demo verdict (e.g. it resolved behind an open dialog). */
  showDemoNotice(vcr: ViewContainerRef): void {
    this.opener.open<ConfirmDialogContext>(ConfirmDialogComponent, vcr, DEMO_NOTICE);
  }

  // The full detail fetch is authoritative — the projects list can be a lightweight projection with `settings: null`.
  async isDemo(projectId: string | null | undefined): Promise<boolean> {
    if (!projectId) return false;
    // Fail open: a transient detail fetch must not block a legitimate download nor leak a rejection.
    try {
      return await this.facade.isProjectDemo(projectId);
    } catch {
      return false;
    }
  }
}
